import { useEffect, useState } from 'react'

/**
 * Trạng thái cài đặt ứng dụng. Hai nền tảng đi hai đường khác hẳn nhau — đây
 * là lý do hook này tồn tại thay vì gọi thẳng `beforeinstallprompt`.
 *
 *   Android / Chrome  → trình duyệt bắn `beforeinstallprompt`, ta giữ lại và
 *                       gọi `.prompt()` khi người dùng bấm nút. Có hộp thoại
 *                       cài thật.
 *   iOS / iPadOS      → Safari KHÔNG hỗ trợ `beforeinstallprompt`. Không có
 *                       API nào cho phép app tự mở hộp thoại cài. Cách duy
 *                       nhất là chỉ người dùng tự bấm Chia sẻ → Thêm vào MH
 *                       chính. Đây là chặn cứng của Apple.
 *
 * Vì iPad là thiết bị được nhắm tới, nhánh `ios` không phải trường hợp phụ —
 * nó là nhánh chính.
 */

export type InstallState =
  | 'unsupported' // trình duyệt không cài được, hoặc chưa đủ điều kiện
  | 'available'   // có `beforeinstallprompt` trong tay, bấm là cài
  | 'ios'         // phải hướng dẫn thủ công
  | 'installed'   // đang chạy ở chế độ đã cài

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'shoptrack:install-dismissed'

/**
 * Từ iPadOS 13, Safari trên iPad khai báo `navigator.platform === 'MacIntel'`
 * y hệt máy Mac để các trang web trả về bản desktop. Chỉ nhìn platform là
 * nhận nhầm iPad thành Mac; phải kết hợp `maxTouchPoints` mới tách được.
 */
function isIosLike(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (/iPhone|iPod/.test(ua)) return true
  if (/iPad/.test(ua)) return true
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
}

/** Đang chạy như một app đã cài (không còn thanh địa chỉ). */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Cách riêng của Safari, không theo chuẩn display-mode.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

export function useInstallPrompt() {
  const [state, setState] = useState<InstallState>('unsupported')
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState(readDismissed)

  useEffect(() => {
    if (isStandalone()) {
      setState('installed')
      return
    }
    if (isIosLike()) {
      setState('ios')
      return
    }

    const onPrompt = (e: Event) => {
      // Chặn thanh mời cài mặc định của Chrome để tự quyết thời điểm hỏi —
      // hỏi ngay lúc người dùng vừa vào thì gần như chắc chắn bị từ chối, mà
      // Chrome không bắn lại sự kiện này sớm đâu.
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
      setState('available')
    }
    const onInstalled = () => {
      setState('installed')
      setDeferred(null)
    }

    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!deferred) return 'unavailable'
    await deferred.prompt()
    const { outcome } = await deferred.userChoice
    // Sự kiện chỉ dùng được MỘT lần. Dùng rồi phải bỏ đi, gọi lại sẽ lỗi.
    setDeferred(null)
    if (outcome === 'accepted') setState('installed')
    return outcome
  }

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // Không lưu được thì chỉ ẩn trong phiên này.
    }
    setDismissed(true)
  }

  return { state, dismissed, promptInstall, dismiss, isIos: state === 'ios' }
}
